import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { BaseButton, Column, Divider, Row, SizedBox } from "../../../common/components/CommonBaseComponents";
import { C3Text, C5Text } from "../../../common/components/text/CustomText";
import CustomRadios from "../../../common/components/radio/CustomRadios";
import CustomInput from "../../../common/components/input/CustomInput";
import CustomIcon from "../../../common/components/image/CustomIcon";
import CustomSunEditor from "../../../common/components/suneditor/CustomSunEditor";
import Translate from "../../../common/translate/Translate";
import { Theme } from "../../../common/Theme";
import SettingsService from "../../../service/SettingsService";
import SecondaryButton from "../../../common/components/button/SecondaryButton";
import PrimaryButton from "../../../common/components/button/PrimaryButton";
import CustomPopup from "../../../common/components/popup/CustomPopup";

const targetOptions = [
    { value: "user", label: Translate("setting.target.user") },
    { value: "admin", label: Translate("setting.target.admin") },
];
const countryValues = [
    { label: Translate("country.select"), value: "select", color: Theme.color.rose500 },
    { label: Translate("country.korea"), value: "korea" },
    { label: Translate("country.chile"), value: "chile" },
    { label: Translate("country.usa"), value: "usa" },
    { label: Translate("country.thailand"), value: "thailand" },
];

const NoticeTabContent = forwardRef(function NoticeTabContent(_, ref) {
    const service = new SettingsService();

    const fileInputRef = useRef(null);

    const [selectedTarget, setSelectedTarget] = useState("user");
    const [noticeContent, setNoticeContent] = useState("");
    const [selectedCountry, setSelectedCountry] = useState("select");
    const [uploadedFile, setUploadedFile] = useState("");
    const [url, setUrl] = useState("");
    const [currentNotice, setCurrentNotice] = useState(null);
    const [detailPageNotice, setDetailPageNotice] = useState("");

    // 국가/대상 선택 시 해당 공지 조회 (spec 04.01.08.04)
    useEffect(() => {
        if (!selectedCountry || selectedCountry === "select") return;
        const targetClientType = selectedTarget === "admin" ? "MANAGER" : "CUSTOMER";
        service
            .getNotice({ country: selectedCountry, targetClientType })
            .then((result) => {
                setCurrentNotice(result);
                setNoticeContent(result.content || "");
                setUrl(result.linkurl || "");
            })
            .catch((error) => {
                console.error("Error fetching notice:", error);
            });
    }, [selectedCountry, selectedTarget]);

    const handleSave = () => {
        if (!noticeContent || !url || !detailPageNotice || !uploadedFile || selectedCountry === "select") return;
        const reqDto = {
            country: selectedCountry,
            targetClientType: selectedTarget === "admin" ? "MANAGER" : "CUSTOMER",
            text: noticeContent,
            linkurl: url,
            content: uploadedFile instanceof File ? uploadedFile : undefined,
        };
        const action = currentNotice ? service.updateNotice(reqDto) : service.saveNotice(reqDto);
        action.catch((error) => console.error("Error saving notice:", error));
    };

    useImperativeHandle(ref, () => ({ save: handleSave }));

    const [isCancelPopupOpen, setIsCancelPopupOpen] = useState(false);

    const handleCancelClose = () => {
        setIsCancelPopupOpen(false);
    };

    const onCancelClick = () => {
        setIsCancelPopupOpen(true);
    };

    const handleCancelOk = () => {
        setIsCancelPopupOpen(false);
        setSelectedTarget("user");
        setNoticeContent("");
        setSelectedCountry("select");
        setUploadedFile(null);
        setUrl("");
        setCurrentNotice(null);
        setDetailPageNotice("");
    };

    return (
        <SizedBox height={"auto"} width={"100%"} maxWidth={"800px"} alignItem={"flex-start"} padding={"32px 0"}>
            <Row width={"100%"} padding={"0 0 0 12px"}>
                <SizedBox width={"150px"} alignItem={"flex-start"} height={"auto"}>
                    <C3Text>{Translate("setting.target")}</C3Text>
                </SizedBox>
                <CustomRadios
                    options={targetOptions}
                    selectedOption={selectedTarget}
                    setSelectedOption={setSelectedTarget}
                />
            </Row>
            <Divider marginTop={"23px"} marginBottom={"11px"} lineStyle={"custom-dotted"} />
            <Row width={"100%"} alignItem={"flex-start"} padding={"0 0 0 12px"}>
                <SizedBox
                    width={"145px"}
                    alignItem={"flex-start"}
                    height={"auto"}
                    justifyContent={"flex-start"}
                    padding={"12px 0"}
                >
                    <C3Text>{Translate("setting.noticeContent")}</C3Text>
                </SizedBox>
                <CustomInput
                    type={"textAria"}
                    placeholder={Translate("placeholder.noticeContent")}
                    inputValue={noticeContent}
                    setInputValue={setNoticeContent}
                />
            </Row>
            <Divider marginTop={"11px"} marginBottom={"11px"} lineStyle={"custom-dotted"} />
            <Row width={"100%"} padding={"0 0 0 12px"}>
                <SizedBox width={"145px"} alignItem={"flex-start"} height={"auto"}>
                    <C3Text>{Translate("country")}</C3Text>
                </SizedBox>
                <CustomInput
                    type={"select"}
                    placeholder={""}
                    dropdownValues={countryValues}
                    inputValue={selectedCountry}
                    setInputValue={setSelectedCountry}
                />
            </Row>
            <Divider marginTop={"11px"} marginBottom={"11px"} lineStyle={"custom-dotted"} />
            <Row width={"100%"} padding={"0 0 0 12px"} justifyContent={"flex-start"} alignItem={"flex-start"}>
                <SizedBox
                    width={"145px"}
                    alignItem={"flex-start"}
                    height={"auto"}
                    justifyContent={"flex-start"}
                    padding={"12px 0"}
                >
                    <C3Text>{Translate("setting.popupNotice")}</C3Text>
                </SizedBox>
                <Column width={"100%"} flex={"1"} justifyContent={"flex-start"} alignItem={"flex-start"}>
                    <Row width={"100%"} justifyContent={"flex-start"} alignItem={"flex-start"}>
                        <Column width={"100%"} flex={"1"} justifyContent={"flex-start"} alignItem={"flex-start"}>
                            <input
                                ref={fileInputRef}
                                type={"file"}
                                accept={"image/*"}
                                style={{ display: "none" }}
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) setUploadedFile(file);
                                }}
                            />
                            <CustomInput
                                type={"file"}
                                placeholder={Translate("placeholder.uploadImage")}
                                inputValue={uploadedFile?.name || ""}
                                setInputValue={setUploadedFile}
                            />
                            <SizedBox height={"6px"} />
                            <C5Text fontColor={Theme.color.gray500}>{Translate("setting.imageDescription")}</C5Text>
                        </Column>
                        <SizedBox width={"10px"} />
                        <BaseButton
                            width={"42px"}
                            height={"42px"}
                            padding={"11px"}
                            border={`1px solid ${Theme.color.gray250}`}
                            borderRadius={"8px"}
                            backgroundColor={Theme.color.white}
                            onClick={() => fileInputRef.current.click()}
                        >
                            <Row alignItem={"center"} cursor={"pointer"}>
                                <CustomIcon iconName={"upload"} size={"17px"} cursor={"pointer"} />
                            </Row>
                        </BaseButton>
                    </Row>
                    <SizedBox height={"11px"} />
                    <Row width={"100%"} justifyContent={"flex-start"} alignItem={"flex-start"}>
                        <Column width={"100%"} flex={"1"} justifyContent={"flex-start"} alignItem={"flex-start"}>
                            <CustomInput
                                type={"normal"}
                                placeholder={Translate("placeholder.url")}
                                inputValue={url}
                                setInputValue={setUrl}
                            />
                            <SizedBox height={"6px"} />
                            <C5Text fontColor={Theme.color.gray500}>{Translate("setting.urlDescription")}</C5Text>
                            <C5Text fontColor={Theme.color.gray500}>
                                ST : https://kic-laundry-st-web.lgthinq.com/NoticeView?country=&clientType=CUSTOMER
                            </C5Text>
                            <C5Text fontColor={Theme.color.gray500}>
                                QA : https://kic-laundry-qa-web.lgthinq.com/NoticeView?country=&clientType=CUSTOMER
                            </C5Text>
                            <C5Text fontColor={Theme.color.gray500}>
                                OP : https://kic-laundry-web.lgthinq.com/NoticeView?country=&clientType=CUSTOMER
                            </C5Text>
                        </Column>
                        <SizedBox width={"52px"} />
                    </Row>
                </Column>
            </Row>
            <Divider marginTop={"11px"} marginBottom={"11px"} lineStyle={"custom-dotted"} />
            <Row width={"100%"} alignItem={"flex-start"} padding={"0 0 0 12px"}>
                <SizedBox
                    width={"145px"}
                    alignItem={"flex-start"}
                    height={"auto"}
                    justifyContent={"flex-start"}
                    padding={"12px 0"}
                >
                    <C3Text>{Translate("setting.detailPageContent")}</C3Text>
                </SizedBox>
                <Row flex={"1"}>
                    <CustomSunEditor setValue={setDetailPageNotice} />
                </Row>
            </Row>
            <SizedBox height={"10px"} />
            <Row width={"100%"} justifyContent={"flex-end"}>
                <SecondaryButton text={Translate("common.cancel")} onClick={onCancelClick} />
                <SizedBox width={"13px"} />
                <PrimaryButton
                    icon={"checked_white"}
                    text={Translate("common.save")}
                    onClick={() => handleSave()}
                    disabled={
                        !noticeContent || !url || !detailPageNotice || !uploadedFile || selectedCountry === "select"
                    }
                />
            </Row>
            <CustomPopup
                isOpen={isCancelPopupOpen}
                onClose={handleCancelClose}
                onConfirm={handleCancelOk}
                message={Translate("popup.cancel")}
            />
        </SizedBox>
    );
});

export default NoticeTabContent;
